<template>
    <Card v-if="card" :header="header" overlay :loading="loading || updating" no-separate-content>
        <template v-if="$slots.header" #header>
            <slot name="header"></slot>
        </template>
        <template v-if="$slots.nav" #nav>
            <slot name="nav"></slot>
        </template>
        <template v-if="$slots.subHeader" #subHeader>
            <slot name="subHeader"></slot>
        </template>
        <ErrorMessage :value="error" />
        <VueApexcharts v-bind="attrs" v-on="listeners" />
    </Card>
    <b-overlay v-else :show="updating" rounded="sm">
        <VueApexcharts v-bind="attrs" v-on="listeners" />
    </b-overlay>
</template>



<script>
    import mergeWith from 'lodash/mergeWith';
    import pl from 'apexcharts/dist/locales/pl.json';
    import en from 'apexcharts/dist/locales/en.json';
    import VueApexcharts from '@/components/common/VueApexcharts.vue';
    // import { stringToColour } from '@/utils/util';
    import { i18n, currentLocale, getLanguageFromLocale } from '@/i18n/i18n';

    const currentLang = getLanguageFromLocale(currentLocale);
    const chartLocales = [pl, en];

    const RANGES = ['hour', 'day', 'week', 'month', 'quarter', 'year'];

    const customizer = (objValue, srcValue) => {
        // Jeśli `srcValue` jest tablicą i `objValue` jest obiektem
        if (typeof objValue === 'object' && !Array.isArray(objValue) && Array.isArray(srcValue)) {
            // Kopiujemy `objValue` i łączymy go z każdym elementem tablicy
            return srcValue.map(item => mergeWith({}, objValue, item, customizer));
        }
        // Dla innych przypadków pozwól Lodashowi działać standardowo
        return undefined;
    };

    export default {
        name: 'Chart',
        components: {
            VueApexcharts,
        },
        props: {
            error: {
                type: [Error, Object],
                default: null,
            },
            header: {
                type: String,
                default: '',
            },
            card: {
                type: Boolean,
                default: false,
            },
            loading: {
                type: Boolean,
                default: false,
            },
            range: {
                type: String,
                default: 'day',
                validator: value => RANGES.includes(value),
            },
            options: {
                type: Object,
                default: () => ({}),
            },
            type: {
                type: String,
            },
            series: {
                type: Array,
                required: true,
                default: () => [],
            },
            width: {
                type: [String, Number],
            },
            height: {
                type: [String, Number],
            },
        },
        data() {
            const baseChartOptions = {
                colors: ['#2e93fa', '#546e7a', '#d4526e', '#13d8aa', '#a5978b', '#4ecdc4', '#c7f464', '#81d4fa', '#546e7a', '#fd6a6a'],
                noData: {
                    text: i18n.t('noData'),
                    align: 'center',
                    verticalAlign: 'middle',
                    offsetX: 0,
                    offsetY: 0,
                    style: {
                        color: '#ccc',
                        fontSize: '30px',
                        fontFamily: undefined,
                    },
                },
                chart: {
                    type: 'area',
                    width: this.width || '100%',
                    height: this.height,
                    toolbar: {
                        show: false,
                    },
                    zoom: {
                        enabled: false,
                    },
                    animations: {
                        enabled: false,
                        speed: 500,
                        animateGradually: {
                            delay: 0,
                        },
                        dynamicAnimation: {
                            enabled: false,
                            speed: 500,
                        },
                    },
                    events: {
                        click: (event, chartContext, config) => {
                            this.$emit('click', { event, chartContext, config }); // The last parameter config contains additional information like `seriesIndex` and `dataPointIndex` for cartesian charts
                        },
                    },
                    stacked: false,
                },
                markers: {
                    size: 0,
                },
                zoom: {
                    enabled: false,
                },
                dataLabels: {
                    enabled: false,
                },
                xaxis: {
                    type: 'datetime',
                    tickAmount: 'dataPoints',
                    tooltip: {
                        enabled: false,
                    },
                    axisBorder: {
                        show: false,
                    },
                    labels: {
                        show: true,
                        datetimeUTC: false,
                        // formatter: (value, timestamp, opts) => this.$format.date(Number.parseInt(value) || 0, this.dateTimeFormat[this.range]),
                    },
                },
                yaxis: {
                    title: {
                        style: {
                            color: undefined,
                            fontSize: '14px',
                            fontFamily: undefined,
                            fontWeight: 300,
                            cssClass: undefined,
                        },
                    },
                },
                legend: {
                    height: 40,
                },
                fill: {},
            };

            if (chartLocales?.length) baseChartOptions.chart.locales = chartLocales;
            if (currentLang) baseChartOptions.chart.defaultLocale = currentLang;

            return {
                baseChartOptions,
                updating: false,
            };
        },
        computed: {
            listeners() {
                return {
                    ...this.$listeners,
                    optionsUpdate: this.optionsUpdate,
                };
            },
            attrs() {
                return {
                    ...this.$attrs,
                    ref: 'chart',
                    type: this.chartType,
                    options: this.chartOptions,
                    height: this.chartOptions.chart.height,
                    width: this.chartOptions.chart.width,
                };
            },
            chartSeries() {
                if (this.series.length) return this.series;
                if (this.options?.series?.length) return this.options.series;
                return [];
            },
            dateTimeFormat() {
                return {
                    hour: 'HH:mm',
                    day: 'dd MMM yyyy',
                    week: `w '${this.$t('week').toLowerCase()}' yyyy`,
                    month: 'MMM yyyy',
                    quarter: 'QQQ yyyy',
                    year: 'yyyy',
                };
            },
            chartType() {
                return this.type || this.options.chart?.type || this.baseChartOptions.chart.type;
            },
            chartComputedOptions() {
                const chartType = this.chartType;
                const count = chartType === 'pie' ? chartType.length : this.chartSeries.filter(item => !!item.data.length).length;
                const options = {
                    // colors: this.chartSeries.length ? this.chartSeries.map((item, index) => (item.name ? stringToColour(item.name) : this.baseChartOptions.colors[index])) : undefined,
                    chart: {
                        type: chartType,
                        defaultLocale: getLanguageFromLocale(this.$i18n.locale),
                    },
                };
                if (!count) {
                    options.xaxis = {
                        labels: {
                            show: false,
                        },
                    };
                }
                if (chartType !== 'heatmap') {
                    options.fill = {
                        opacity: this.chartSeries.map(item => (item.type === 'area' || (!item.type && chartType === 'area') ? undefined : 1)),
                        type: this.chartSeries.map(item => (item.type === 'area' || (!item.type && chartType === 'area') ? 'gradient' : 'solid')),
                    };
                }
                if (chartType === 'area' || chartType === 'line') {
                    options.stroke = {
                        show: true,
                        curve: 'smooth',
                        width: 2,
                    };
                }
                if (chartType === 'rangeBar') {
                    options.tooltip = {
                        custom: opts => {
                            const w = opts.ctx.w;
                            const color = w.globals.colors[opts.seriesIndex];
                            const seriesName = w.config.series[opts.seriesIndex].name ? w.config.series[opts.seriesIndex].name : '';
                            // const ylabel = w.config.series[opts.seriesIndex].data?.[opts.dataPointIndex]?.x;
                            const from = this.$format.date(opts.y1);
                            const to = this.$format.date(opts.y2);
                            return `<div class="apexcharts-tooltip-rangebar">
                                        <div>
                                            <span class="series-name" style="color: ${color}">${seriesName || ''}</span>
                                        </div>
                                        <div>
                                            <span class="value start-value">${from}</span>
                                            <span class="separator">-</span>
                                            <span class="value end-value">${to}</span>
                                        </div>
                                        <div>
                                            <span class="value-period">
                                                <strong>${this.$t('duration')}:</strong> ${this.$format.duration(opts.y1, opts.y2)}
                                            </span>
                                        </div>
                                    </div>`;
                        },
                    };
                    if (count) {
                        if (this.options?.plotOptions?.bar?.rangeBarGroupRows) {
                            const height = Math.max(144, count * 50 + 70);
                            options.chart.height = height;
                        }
                        options.yaxis = {
                            labels: {
                                style: {
                                    fontSize: '14px',
                                    fontWeight: '600',
                                },
                            },
                        };
                    }
                }
                if (chartType === 'pie') {
                    options.dataLabels = {
                        enabled: true,
                        formatter: val => this.$format.percent(val, 0),
                    };
                    options.legend = {
                        show: true,
                        showForSingleSeries: false,
                        showForNullSeries: true,
                        showForZeroSeries: true,
                        position: 'bottom',
                        height: 60,
                    };
                }

                const xaxisType = this.options?.xaxis?.type || this.baseChartOptions.xaxis.type;

                if (xaxisType === 'datetime') {
                    if (!options.xaxis) options.xaxis = {};
                    options.xaxis.type = 'category';
                    if (!this.options?.xaxis?.type?.labels?.formatter) {
                        if (!options.xaxis.labels) options.xaxis.labels = {};
                        options.xaxis.labels.formatter = (value, timestamp, opts) => this.$format.date(Number.parseInt(value) || 0, this.dateTimeFormat[this.range]);
                    }
                }
                return options;
            },
            chartOptions() {
                return mergeWith({}, this.baseChartOptions, this.chartComputedOptions, this.options, { series: this.series }, customizer);
            },
        },
        watch: {
            '$i18n.locale': {
                handler() {
                    const locale = getLanguageFromLocale(this.$i18n.locale);
                    this.$refs.chart.setLocale(locale);
                },
            },
        },
        methods: {
            optionsUpdate(busy) {
                this.$emit('update', busy);
                this.updating = busy;
            },
        },
    };
</script>
